// #region --------------- Contact React Query hooks ----------------------

import {
  createMatterRoleV1,
  deleteMatterRoleV1,
  updateMatterRoleV1,
} from "@/lib/data/requests";
import { type MatterSettings } from "@/lib/data/requests/matters/matter-settings";
import { queryKeys } from "@/lib/data/static";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";

export const useCreateMatterRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterRoleV1>[0]) =>
      createMatterRoleV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              roles: orderBy(
                [...oldData.data.roles, { ...data, id: "temp" }],
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating matter role");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter role created successfully");
    },
  });
};

export const useUpdateMatterRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterRoleV1>[0]) =>
      updateMatterRoleV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;

          return {
            data: {
              ...oldData.data,
              roles: orderBy(
                oldData.data.roles.map((group) =>
                  group.id === data.id ? { ...data, id: data.id } : group,
                ),
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter role");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter role updated successfully");
    },
  });
};

export const useDeleteMatterRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterRoleV1>[0]) =>
      deleteMatterRoleV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              roles: oldData.data.roles.filter((group) => group.id !== data.id),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting matter role");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter role deleted successfully");
    },
  });
};
